import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CategoryCard } from "@/ds";
import api from "@/lib/api";
import { categoryLabel } from "@/lib/catalog";

/**
 * Data-driven grid of every active service category, on Fable (DY1.4).
 *
 * Categories come from the public catalog (GET /catalog/categories) — nothing is
 * hardcoded, so seeding a new category makes it appear here automatically. Its
 * glyph comes with it, in `icon` (the row's own column since REG 2026-10-29);
 * a row with none draws `CATEGORY_ICON_FALLBACK` rather than breaking the grid.
 *
 * One `auto-fit` track, not `grid-cols-1 sm:2 lg:3 xl:4`: a fixed column count
 * crushes cards once Spanish copy wraps (MIGRATION.md, Responsive).
 */
export default function CategoryGrid() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let alive = true;
    api
      .get("/catalog/categories")
      .then((res) => {
        if (!alive) return;
        setCategories(res.data.categories || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (categories.length === 0) return null;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
        gap: "var(--card-gap)",
      }}
      data-testid="category-grid"
    >
      {categories.map((c) => (
        <CategoryCard
          key={c.category_id}
          icon={c.icon}
          label={categoryLabel(t, c)}
          onClick={() => navigate(`/providers?category=${c.category_id}`)}
          data-testid={`category-card-${c.category_id}`}
        />
      ))}
    </div>
  );
}
